import { useEffect, useRef } from 'react'
import { BsChevronDown, BsChevronUp, BsEye, BsEyeSlash } from 'react-icons/bs'
import { StatField } from '../stat-field/stat-field'
import './stat-row.scss'
import { EditableField, MoveDirection, StatItem } from '../../../../../Models'
import { validateStat, DigitCommaDotFormatter } from '../../../../../Helpers'
import { FIELD_LIMITS } from '../../../../../Constants/Stats'

interface StatRowProps {
  item: StatItem
  index: number
  total: number
  isEditing: boolean
  showErrors: boolean
  onEdit: (id: string) => void
  onDone: () => void
  onChange: (id: string, field: EditableField, value: string) => void
  onBlur: (id: string) => void
  onMove: (id: string, direction: MoveDirection) => void
  onToggleVisibility: (id: string) => void
}

/** "$" + "1.2" + "M" -> "$1.2M". Falls back to a dash while the number is empty. */
export function formatStatValue(item: Pick<StatItem, 'prefix' | 'number' | 'suffix'>): string {
  const number = item.number.trim() || '-'
  return `${item.prefix.trim()}${number}${item.suffix.trim()}`
}

export function StatRow({
  item,
  index,
  total,
  isEditing,
  showErrors,
  onEdit,
  onDone,
  onChange,
  onBlur,
  onMove,
  onToggleVisibility,
}: StatRowProps) {
  const numberInputRef = useRef<HTMLInputElement>(null)
  const editButtonRef = useRef<HTMLButtonElement>(null)
  const wasEditing = useRef(false)

  // Move focus into the form when it opens and back to the trigger when it closes.
  useEffect(() => {
    if (isEditing) {
      numberInputRef.current?.focus()
    } else if (wasEditing.current) {
      editButtonRef.current?.focus()
    }
    wasEditing.current = isEditing
  }, [isEditing])

  const errors = showErrors ? validateStat(item) : {}
  const panelId = `stat-row-panel-${item.id}`
  const isFirst = index === 0
  const isLast = index === total - 1

  const rootClassName = [
    'stat-row',
    isEditing ? 'stat-row--editing' : '',
    item.visible ? '' : 'stat-row--hidden',
  ]
    .filter(Boolean)
    .join(' ')

  const eyeLabel = item.visible
    ? `Hide ${item.label} from landing page`
    : `Show ${item.label} on landing page`

  return (
    <li className={rootClassName}>
      <div className='stat-row__header'>
        <span className={`stat-row__badge stat-row__badge--${item.color}`} aria-hidden='true'>
          {index + 1}
        </span>

        <div className='stat-row__summary'>
          <p className='stat-row__title'>
            {item.label}
            {!item.visible && <span className='stat-row__status'>Hidden</span>}
          </p>
          <p className='stat-row__meta'>
            <strong className='stat-row__value'>{formatStatValue(item)}</strong>
            <span>·</span>
            <span className='stat-row__description'>{item.description.trim()}</span>
          </p>
        </div>

        <div className='stat-row__actions'>
          <button
            type='button'
            className='stat-row__icon-btn'
            aria-label={`Move ${item.label} up`}
            disabled={isFirst}
            onClick={() => onMove(item.id, 'up')}
          >
            <BsChevronUp aria-hidden='true' />
          </button>
          <button
            type='button'
            className='stat-row__icon-btn'
            aria-label={`Move ${item.label} down`}
            disabled={isLast}
            onClick={() => onMove(item.id, 'down')}
          >
            <BsChevronDown aria-hidden='true' />
          </button>
          <button
            type='button'
            className='stat-row__icon-btn'
            aria-label={eyeLabel}
            aria-pressed={!item.visible}
            title={eyeLabel}
            onClick={() => onToggleVisibility(item.id)}
          >
            {item.visible ? <BsEye aria-hidden='true' /> : <BsEyeSlash aria-hidden='true' />}
          </button>

          {isEditing ? (
            <button
              type='button'
              className='stat-row__text-btn stat-row__text-btn--primary'
              aria-controls={panelId}
              aria-expanded='true'
              onClick={onDone}
            >
              Done
            </button>
          ) : (
            <button
              ref={editButtonRef}
              type='button'
              className='stat-row__text-btn'
              aria-controls={panelId}
              aria-expanded='false'
              aria-label={`Edit ${item.label}`}
              onClick={() => onEdit(item.id)}
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {isEditing && (
        <form
          id={panelId}
          className='stat-row__form'
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            onDone()
          }}
        >
          <div className='stat-row__grid'>
            <div className='stat-row__cell'>
              <StatField
                label='Prefix'
                hint='e.g. $, ₦'
                value={item.prefix}
                placeholder='$'
                maxLength={FIELD_LIMITS.prefix}
                error={errors.prefix}
                onChange={(value) => onChange(item.id, 'prefix', value)}
                onBlur={() => onBlur(item.id)}
              />
            </div>
            <div className='stat-row__cell'>
              <StatField
                label='Number'
                required
                value={item.number}
                placeholder='1.2'
                inputMode='decimal'
                maxLength={FIELD_LIMITS.number}
                error={errors.number}
                inputRef={numberInputRef}
                onChange={(value) => onChange(item.id, 'number', DigitCommaDotFormatter(value))}
                onBlur={() => onBlur(item.id)}
              />
            </div>
            <div className='stat-row__cell'>
              <StatField
                label='Suffix'
                hint='e.g. M, +, MW'
                value={item.suffix}
                placeholder='M'
                maxLength={FIELD_LIMITS.suffix}
                error={errors.suffix}
                onChange={(value) => onChange(item.id, 'suffix', value)}
                onBlur={() => onBlur(item.id)}
              />
            </div>
            <div className='stat-row__cell stat-row__cell--full'>
              <StatField
                label='Description'
                required
                value={item.description}
                placeholder='What does this number represent?'
                maxLength={FIELD_LIMITS.description}
                showCounter
                error={errors.description}
                onChange={(value) => onChange(item.id, 'description', value)}
                onBlur={() => onBlur(item.id)}
              />
            </div>
          </div>
          {/* Lets Enter submit the form from any single-line field. */}
          <button type='submit' hidden tabIndex={-1} aria-hidden='true' />
        </form>
      )}
    </li>
  )
}
